import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, Image as ImageIcon, Clock, Upload, Download, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Logo, Loader, StatusBadge } from "@/components/Brand";
import { AuthImage } from "@/components/AuthImage";
import { formatDate } from "@/lib/format";
import { fileDownloadUrl, uploadToProject, humanSize } from "@/lib/files";

const DOC_TYPES = [
  ["drawing", "Drawing"], ["contract", "Contract"], ["certificate", "Certificate"],
  ["invoice", "Invoice"], ["planning", "Planning"], ["other", "Other"],
];

export default function ClientProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [bundle, setBundle] = useState(null);
  const [tab, setTab] = useState("overview");

  const load = () => api.get(`/projects/${projectId}`).then(({ data }) => setBundle(data))
    .catch(() => { toast.error("Project not found"); navigate("/dashboard"); });
  useEffect(() => { load(); }, [projectId]);

  if (!bundle) return <div className="min-h-screen bg-[#090A0C]"><Loader /></div>;
  const { project, documents, photos, updates } = bundle;
  const stages = project.stages || [];
  const currentIdx = stages.findIndex((s) => s.name === project.status);

  return (
    <div className="min-h-screen bg-[#090A0C] text-slate-50">
      <header className="border-b border-slate-800/80">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-4">
          <Logo size="text-lg" />
          <button onClick={() => navigate("/dashboard")} className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-amber-400" data-testid="project-back-btn"><ArrowLeft className="h-4 w-4" /> Dashboard</button>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-4 py-8">
        <div className="evo-fade-up flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl font-bold sm:text-3xl">{project.name}</h1>
            <p className="mt-1 text-sm text-slate-400">Started {formatDate(project.created_at)}</p>
          </div>
          <StatusBadge status={project.status} />
        </div>

        <div className="mt-6 flex gap-2 border-b border-slate-800">
          {[["overview", "Timeline", Clock], ["photos", `Photos (${photos.length})`, ImageIcon], ["documents", `Documents (${documents.length})`, FileText]].map(([k, label, Icon]) => (
            <button key={k} onClick={() => setTab(k)} className={`flex items-center gap-2 border-b-2 px-3 py-2.5 text-sm font-medium ${tab === k ? "border-amber-500 text-amber-400" : "border-transparent text-slate-400 hover:text-slate-200"}`} data-testid={`project-tab-${k}`}>
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div>
              <h2 className="font-heading text-lg font-semibold">Project timeline</h2>
              <div className="mt-4 space-y-1">
                {stages.map((s, i) => {
                  const done = currentIdx >= 0 && i < currentIdx;
                  const active = i === currentIdx;
                  return (
                    <div key={s.id} className="flex items-start gap-3" data-testid={`timeline-stage-${s.name}`}>
                      <div className="flex flex-col items-center">
                        {done ? <CheckCircle2 className="h-5 w-5 text-emerald-400" /> : active ? <div className="h-5 w-5 rounded-full border-2 border-amber-500 bg-amber-500/20" /> : <Circle className="h-5 w-5 text-slate-700" />}
                        {i < stages.length - 1 && <div className={`my-1 h-6 w-0.5 ${done ? "bg-emerald-400/40" : "bg-slate-800"}`} />}
                      </div>
                      <span className={`pt-0.5 text-sm ${active ? "font-semibold text-amber-400" : done ? "text-slate-300" : "text-slate-500"}`}>{s.name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div>
              <h2 className="font-heading text-lg font-semibold">Site updates</h2>
              <div className="mt-4 space-y-3">
                {updates.length === 0 && <p className="text-sm text-slate-500">No updates yet. Your project manager will post progress here.</p>}
                {updates.map((u) => (
                  <div key={u.id} className="rounded-xl border border-slate-800 bg-[#151820] p-4" data-testid={`update-${u.id}`}>
                    <div className="flex items-center justify-between">
                      <p className="font-semibold">{u.title}</p>
                      <span className="text-xs text-slate-500">{formatDate(u.created_at)}</span>
                    </div>
                    <p className="mt-1 text-sm text-slate-300">{u.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "photos" && (
          <div className="mt-6">
            {photos.length === 0 ? <Empty icon={ImageIcon} body="Progress photos from site will appear here." /> : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {photos.map((p) => (
                  <a key={p.id} href={fileDownloadUrl(p.id)} target="_blank" rel="noreferrer" className="group relative overflow-hidden rounded-xl border border-slate-800" data-testid={`photo-${p.id}`}>
                    <AuthImage fileId={p.id} alt={p.caption} className="aspect-square w-full object-cover transition-transform group-hover:scale-105" />
                    {p.caption && <div className="absolute inset-x-0 bottom-0 bg-black/60 p-2 text-xs text-slate-200">{p.caption}</div>}
                  </a>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "documents" && (
          <DocumentsTab documents={documents} projectId={projectId} onChange={load} />
        )}
      </div>
    </div>
  );
}

function DocumentsTab({ documents, projectId, onChange }) {
  const [docType, setDocType] = useState("other");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { await uploadToProject({ projectId, kind: "document", file, docType }); toast.success("Document uploaded"); onChange(); }
    catch (err) { toast.error(err.message); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const del = async (id) => { if (window.confirm("Remove this document?")) { await api.delete(`/files/${id}`); onChange(); } };

  return (
    <div className="mt-6">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-800 bg-[#151820] p-4">
        <select value={docType} onChange={(e) => setDocType(e.target.value)} className="rounded-lg border border-slate-700 bg-[#0d0f13] px-3 py-2 text-sm" data-testid="doc-type-select">
          {DOC_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input ref={fileRef} type="file" onChange={onFile} className="hidden" data-testid="doc-file-input" />
        <button onClick={() => fileRef.current?.click()} disabled={uploading} className="flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-bold text-slate-950 hover:bg-amber-500 disabled:opacity-60" data-testid="doc-upload-btn">
          <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : "Upload document"}
        </button>
      </div>

      <div className="mt-4 space-y-2">
        {documents.length === 0 && <Empty icon={FileText} body="Drawings, contracts, certificates and other project documents will appear here." />}
        {documents.map((d) => (
          <div key={d.id} className="flex items-center justify-between rounded-xl border border-slate-800 bg-[#151820] p-4" data-testid={`document-${d.id}`}>
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-800"><FileText className="h-5 w-5 text-amber-400" /></div>
              <div className="min-w-0">
                <p className="truncate font-medium">{d.original_filename}</p>
                <p className="text-xs text-slate-500"><span className="uppercase">{d.doc_type}</span> · {humanSize(d.size)} · {formatDate(d.created_at)} {d.uploaded_by_role === "admin" ? "· EvoStruct" : "· You"}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <a href={fileDownloadUrl(d.id)} target="_blank" rel="noreferrer" className="rounded-lg border border-slate-700 p-2 text-slate-300 hover:border-amber-500/50" data-testid={`download-doc-${d.id}`}><Download className="h-4 w-4" /></a>
              {d.uploaded_by_role !== "admin" && <button onClick={() => del(d.id)} className="rounded-lg border border-slate-700 p-2 text-slate-500 hover:text-rose-400">✕</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Empty({ icon: Icon, body }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-800 bg-[#111318] px-6 py-14 text-center">
      <Icon className="h-9 w-9 text-slate-600" />
      <p className="mt-3 max-w-sm text-sm text-slate-400">{body}</p>
    </div>
  );
}
